import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { differenceBy, isEmpty, orderBy } from 'lodash';
import { format } from 'date-fns';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

import formatPatientAddress from 'common/utils/formatPatientAddress';

export const INHERIT_VALUE = '';

const isShippable = (address) => !!address?.addressDirectoryId;

const isUnverified = (address) => isShippable(address) && address.isVerified === false;

const addressFor = (addresses, addressDirectoryId) =>
  addresses.find((address) => address.addressDirectoryId === addressDirectoryId);

const lastUsedNote = (address) => {
  if (!address?.lastUsedTimestamp) return 'Not used yet';

  const used = new Date(address.lastUsedTimestamp);
  if (Number.isNaN(used.getTime())) return undefined;

  return `Last used ${format(used, 'MM-dd-yyyy')}`;
};

const menuNote = (address) => {
  if (!isShippable(address)) return 'Not verified for shipping';
  if (isUnverified(address)) return 'Could not verify this address';

  return lastUsedNote(address);
};

export const hasShippableAddresses = (addresses) => !isEmpty(addresses.filter(isShippable));

export const isUnverifiedSelection = (params) => {
  const { addresses, addressDirectoryId } = params;
  if (!addressDirectoryId) return false;

  return isUnverified(addressFor(addresses, addressDirectoryId));
};

const InlineTag = ({ label: text }) => (
  <Chip
    size='small'
    label={text}
    sx={{ ml: 1, height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
  />
);

InlineTag.propTypes = { label: PropTypes.string.isRequired };

const FillLaterAddressField = ({
  id,
  label,
  addresses,
  value,
  onChange,
  onBlockedChange,
  onAcknowledgedChange,
  inheritedAddress,
  currentValue,
  currentAddress,
  disabled,
  loading,
  size,
}) => {
  const previousAddresses = useRef(addresses);
  const [acknowledged, setAcknowledged] = useState(false);

  const inheritedText = inheritedAddress ? formatPatientAddress(inheritedAddress) : '';

  // The order's own address is usually one of the patient's addresses too, so it arrives twice:
  // once as the inherit row and once in the list below it. Two identical lines, one of which
  // quietly means something different, reads as a bug and invites the wrong one. The list copy
  // goes - unless it is the current selection, where dropping it would leave the field showing a
  // value with no row behind it.
  const ordered = orderBy(
    addresses,
    [(address) => address.lastUsedTimestamp ?? ''],
    ['desc']
  ).filter(
    (address) =>
      !inheritedText ||
      address.addressDirectoryId === value ||
      formatPatientAddress(address) !== inheritedText
  );
  const selectedAddress = addressFor(addresses, value);
  const selectedIsUnverified = isUnverified(selectedAddress);
  const blocked = selectedIsUnverified && !acknowledged;
  const marksCurrent = currentValue !== undefined;

  // A chosen address can drop out of the patient's list between scheduling and amending -
  // addPatientAddress tombstones the old row and mints a new directory id on every edit, so this
  // is ordinary, not exotic. Clearing the selection to match the list would flip the dialog to
  // "changed" with no operator action and let Save silently revert the fill to the order's
  // address, so the selection is kept and given a row of its own instead.
  const selectionMissing = Boolean(value) && !loading && !isEmpty(addresses) && !selectedAddress;

  useEffect(() => {
    const previous = previousAddresses.current;
    previousAddresses.current = addresses;

    // The first load is not an addition. Without this the whole list reads as new and the first
    // address is selected for the operator, silently sending the fill somewhere nobody chose.
    if (isEmpty(previous)) return;

    const [newAddress] = differenceBy(addresses, previous, 'patientAddressId');
    if (newAddress && isShippable(newAddress)) onChange(newAddress.addressDirectoryId);
  }, [addresses, onChange]);

  useEffect(() => setAcknowledged(false), [value]);

  useEffect(() => {
    if (onBlockedChange) onBlockedChange(blocked);
  }, [blocked, onBlockedChange]);

  // The acknowledgement is what the server records as the operator overriding verification, so it
  // has to leave this component. Only meaningful for an address the provider could not verify:
  // ticking it for a verified one would write an override nobody performed.
  useEffect(() => {
    if (onAcknowledgedChange) onAcknowledgedChange(selectedIsUnverified && acknowledged);
  }, [selectedIsUnverified, acknowledged, onAcknowledgedChange]);

  const labelId = `${id}-label`;

  if (loading) {
    return (
      <Box>
        <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
          {label}
        </Typography>
        <Skeleton variant='rounded' height={size === 'small' ? 40 : 56} />
        <Stack direction='row' alignItems='center' spacing={0.75} sx={{ mt: 0.75 }}>
          <CircularProgress size={12} thickness={5} />
          <Typography variant='caption' color='text.secondary'>
            Loading addresses
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth size={size} disabled={disabled}>
        <InputLabel id={labelId} shrink>
          {label}
        </InputLabel>
        <Select
          labelId={labelId}
          id={id}
          label={label}
          notched
          displayEmpty
          value={value ?? INHERIT_VALUE}
          onChange={(event) => onChange(event.target.value || null)}
          renderValue={(selected) => {
            const picked = selected ? addressFor(addresses, selected) : null;

            if (picked) return formatPatientAddress(picked);
            if (selected) return formatPatientAddress(currentAddress) || 'Address no longer listed';

            return formatPatientAddress(inheritedAddress) || 'Same as the original order';
          }}
          MenuProps={{ PaperProps: { style: { maxHeight: 280 } } }}
        >
          {selectionMissing && (
            <MenuItem value={value}>
              <ListItemText
                primary={
                  <>
                    {formatPatientAddress(currentAddress) || 'The address this fill was given'}
                    <InlineTag label='Current' />
                  </>
                }
                secondary="No longer in this patient's address list"
                primaryTypographyProps={{ variant: 'body2', component: 'div' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </MenuItem>
          )}

          <MenuItem value={INHERIT_VALUE}>
            <ListItemText
              primary={
                <>
                  {inheritedText || 'Same as the original order'}
                  <InlineTag label='Original order' />
                  {marksCurrent && !currentValue && <InlineTag label='Current' />}
                </>
              }
              primaryTypographyProps={{ variant: 'body2', component: 'div' }}
            />
          </MenuItem>

          {ordered.map((address) => (
            <MenuItem
              key={address.patientAddressId}
              value={address.addressDirectoryId ?? address.patientAddressId}
              disabled={!isShippable(address)}
            >
              <ListItemText
                primary={
                  <>
                    {formatPatientAddress(address)}
                    {marksCurrent && currentValue === address.addressDirectoryId && (
                      <InlineTag label='Current' />
                    )}
                  </>
                }
                secondary={menuNote(address)}
                primaryTypographyProps={{ variant: 'body2', component: 'div' }}
                secondaryTypographyProps={{
                  variant: 'caption',
                  color: isUnverified(address) ? 'error.main' : undefined,
                }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedIsUnverified && (
        <Box sx={{ mt: 1 }}>
          <Stack direction='row' alignItems='center' spacing={0.5}>
            <WarningAmberIcon sx={{ color: 'error.main', fontSize: 14 }} />
            <Typography variant='caption' color='error.main' sx={{ fontWeight: 600 }}>
              Could not verify address
            </Typography>
          </Stack>
          <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.5 }}>
            We were unable to verify this address. Please review it carefully and confirm its
            accuracy before scheduling a fill to it.
          </Typography>
          <FormControlLabel
            sx={{ mt: 0.25 }}
            control={
              <Checkbox
                size='small'
                checked={acknowledged}
                onChange={(event) => setAcknowledged(event.target.checked)}
                inputProps={{ 'aria-label': 'Mark address as verified and continue' }}
              />
            }
            label={<Typography variant='caption'>Mark address as verified and continue</Typography>}
          />
        </Box>
      )}
    </Box>
  );
};

FillLaterAddressField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  addresses: PropTypes.arrayOf(
    PropTypes.shape({
      patientAddressId: PropTypes.string,
      addressDirectoryId: PropTypes.string,
      isVerified: PropTypes.bool,
      lastUsedTimestamp: PropTypes.string,
      address: PropTypes.shape({}),
    })
  ),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlockedChange: PropTypes.func,
  onAcknowledgedChange: PropTypes.func,
  inheritedAddress: PropTypes.shape({}),
  currentValue: PropTypes.string,
  currentAddress: PropTypes.shape({}),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
};

FillLaterAddressField.defaultProps = {
  label: 'Ship to',
  addresses: [],
  value: null,
  onBlockedChange: undefined,
  onAcknowledgedChange: undefined,
  inheritedAddress: null,
  currentValue: undefined,
  currentAddress: null,
  disabled: false,
  loading: false,
  size: 'small',
};

export default FillLaterAddressField;
